import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search,
  Download,
  Refresh,
  TrendingUp,
  TrendingDown,
  Wallet,
  DirectionsCar,
  Receipt,
  CalendarToday,
  FilterList,
  Visibility
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { API_URL } from './config';

const DriverEarningsPanel = () => {
  const [tabValue, setTabValue] = useState(0);
  const [earningsData, setEarningsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null
  });
  const [periodFilter, setPeriodFilter] = useState(7);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [driverDetails, setDriverDetails] = useState(null);

  useEffect(() => {
    fetchAllDriversEarnings();
  }, [periodFilter]);

  const fetchAllDriversEarnings = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching drivers earnings for period:', periodFilter, 'days');
      
      // Try to get real data from API
      const apiUrl = `${API_URL}/commission/analytics/drivers-summary?days=${periodFilter}`;
      console.log('📡 API URL:', apiUrl);
      
      const response = await fetch(apiUrl);
      console.log('📡 Response status:', response.status);
      
      // Check if response is OK and is JSON (not HTML)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON');
      }
      
      const data = await response.json();
      console.log('📊 API Response data:', data);
      
      if (data.status === 200) {
        console.log('✅ Success! Drivers data:', data.drivers);
        setEarningsData(data.drivers || []);
      } else {
        throw new Error(`API returned status: ${data.status}`);
      }
    } catch (error) {
      console.error('❌ Error fetching drivers earnings:', error);
      console.log('🔍 Error details:', {
        message: error.message,
        stack: error.stack,
        API_URL: API_URL
      });
      
      // Don't use mock data - show error message instead
      setEarningsData([]);
      
      setAlert({
        show: true,
        type: 'error',
        message: 'Unable to load driver earnings data. Please check if the backend API is implemented and running.'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDetails = async (driverId) => {
    try {
      setLoading(true);
      console.log('🔍 Fetching details for driver:', driverId);
      
      const response = await fetch(`${API_URL}/commission/earnings/${driverId}?days=30`);
      console.log('📡 Driver details response status:', response.status);
      
      // Check if response is OK and is JSON
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON');
      }
      
      const data = await response.json();
      console.log('📊 Driver details data:', data);
      
      if (data.status === 200) {
        console.log('✅ Driver details loaded:', data.earnings);
        setDriverDetails(data.earnings);
      } else {
        throw new Error(`API returned status: ${data.status}`);
      }
    } catch (error) {
      console.error('❌ Error fetching driver details:', error);
      console.log('🔍 Driver details error:', {
        message: error.message,
        driverId: driverId,
        API_URL: API_URL
      });
      
      // Don't use mock data - clear driver details and show error
      setDriverDetails(null);
      
      setAlert({
        show: true,
        type: 'error',
        message: 'Unable to load driver details. Please check if the backend API is implemented and running.'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const prepareChartData = () => {
    if (!driverDetails?.daily_breakdown || driverDetails.daily_breakdown.length === 0) {
      return [];
    }

    return driverDetails.daily_breakdown
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(item => ({
        date: formatDate(item.date),
        earnings: parseFloat(item.total_earnings || 0),
        commissions: parseFloat(item.total_commissions || 0),
        fares: parseFloat(item.total_fares || 0),
        trips: item.trip_count || 0
      }));
  };

  const prepareTopPerformersData = () => {
    if (!earningsData || earningsData.length === 0) {
      return [];
    }

    return earningsData
      .sort((a, b) => b.total_earnings - a.total_earnings)
      .slice(0, 10)
      .map(driver => ({
        name: driver.name || `Driver ${driver.id}`,
        earnings: parseFloat(driver.total_earnings || 0),
        trips: driver.total_trips || 0,
        commission: parseFloat(driver.total_commissions || 0)
      }));
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredDrivers = earningsData.filter(driver =>
    (driver.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (driver.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    driver.id.toString().includes(searchTerm)
  );

  const handleViewDriver = (driver) => {
    setSelectedDriver(driver);
    fetchDriverDetails(driver.id);
    setTabValue(1); // Switch to details tab
  };

  const handleExportData = () => {
    // Export functionality
    const csvContent = [
      ['Driver ID', 'Name', 'Email', 'Total Trips', 'Total Fares', 'Total Commissions', 'Total Earnings', 'Commission Type'],
      ...filteredDrivers.map(driver => [
        driver.id,
        driver.name || '',
        driver.email || '',
        driver.total_trips || 0,
        formatCurrency(driver.total_fares),
        formatCurrency(driver.total_commissions),
        formatCurrency(driver.total_earnings),
        driver.commission_preference || 'percentage'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `driver_earnings_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const renderSummaryCards = () => {
    const totalDrivers = earningsData.length;
    const totalEarnings = earningsData.reduce((sum, driver) => sum + parseFloat(driver.total_earnings || 0), 0);
    const totalCommissions = earningsData.reduce((sum, driver) => sum + parseFloat(driver.total_commissions || 0), 0);
    const totalTrips = earningsData.reduce((sum, driver) => sum + (driver.total_trips || 0), 0);

    console.log('📊 Rendering summary cards:', {
      totalDrivers,
      totalEarnings,
      totalCommissions,
      totalTrips,
      earningsDataLength: earningsData.length,
      earningsData: earningsData
    });

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DirectionsCar sx={{ mr: 1, color: '#1976d2' }} />
                <Typography variant="h6" color="#1976d2">
                  {totalDrivers}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Active Drivers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#e8f5e8' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Wallet sx={{ mr: 1, color: '#2e7d32' }} />
                <Typography variant="h6" color="#2e7d32">
                  {formatCurrency(totalEarnings)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Earnings
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Receipt sx={{ mr: 1, color: '#f57c00' }} />
                <Typography variant="h6" color="#f57c00">
                  {formatCurrency(totalCommissions)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Commissions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#fce4ec' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp sx={{ mr: 1, color: '#c2185b' }} />
                <Typography variant="h6" color="#c2185b">
                  {totalTrips}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Trips
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderDriversTable = () => {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="600">
              Driver Earnings Overview
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search drivers..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                }}
                sx={{ width: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  label="Period"
                >
                  <MenuItem value={7}>Last 7 Days</MenuItem>
                  <MenuItem value={14}>Last 14 Days</MenuItem>
                  <MenuItem value={30}>Last 30 Days</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchAllDriversEarnings}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExportData}
              >
                Export
              </Button>
            </Box>
          </Box>

          {loading ? (
            <LinearProgress sx={{ mb: 2 }} />
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Driver ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Trips</TableCell>
                    <TableCell align="right">Total Fares</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell align="right">Net Earnings</TableCell>
                    <TableCell align="center">Commission Type</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDrivers.map((driver) => (
                    <TableRow key={driver.id} hover>
                      <TableCell>{driver.id}</TableCell>
                      <TableCell>{driver.name || `Driver ${driver.id}`}</TableCell>
                      <TableCell>{driver.email || 'N/A'}</TableCell>
                      <TableCell align="right">{driver.total_trips || 0}</TableCell>
                      <TableCell align="right">{formatCurrency(driver.total_fares)}</TableCell>
                      <TableCell align="right">{formatCurrency(driver.total_commissions)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600" color="success.main">
                          {formatCurrency(driver.total_earnings)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={driver.commission_preference === 'daily' ? 'Daily' : 'Percentage'}
                          color={driver.commission_preference === 'daily' ? 'success' : 'primary'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDriver(driver)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderDriverDetails = () => {
    if (!selectedDriver || !driverDetails) {
      return (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" mb={2}>
                No Driver Data Available
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Please select a driver from the Overview tab to view detailed earnings.
              </Typography>
              <Typography variant="body2" color="info.main" sx={{ fontStyle: 'italic' }}>
                Note: Backend API must be implemented to show real driver earnings data.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const chartData = prepareChartData();

    return (
      <Box>
        {/* Driver Info Header */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" fontWeight="600">
                  {selectedDriver.name || `Driver ${selectedDriver.id}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedDriver.email || 'N/A'} • ID: {selectedDriver.id}
                </Typography>
              </Box>
              <Chip
                label={selectedDriver.commission_preference === 'daily' ? 'Daily Commission' : 'Percentage Commission'}
                color={selectedDriver.commission_preference === 'daily' ? 'success' : 'primary'}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main" fontWeight="700">
                  {formatCurrency(driverDetails.total_earnings)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Net Earnings (30 days)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary.main" fontWeight="700">
                  {formatCurrency(driverDetails.total_fares)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Fares
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="warning.main" fontWeight="700">
                  {formatCurrency(driverDetails.total_commissions)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Commissions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="info.main" fontWeight="700">
                  {driverDetails.total_trips}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Trips
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="600" mb={2}>
                  Earnings Trend (Last 30 Days)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="earnings" stroke="#2e7d32" strokeWidth={2} />
                    <Line type="monotone" dataKey="commissions" stroke="#f57c00" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="600" mb={2}>
                  Daily Breakdown
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.slice(-7)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="earnings" fill="#2e7d32" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderTopPerformers = () => {
    const topPerformers = prepareTopPerformersData();

    if (topPerformers.length === 0) {
      return (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" mb={2}>
                No Driver Performance Data Available
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Unable to load top performers data.
              </Typography>
              <Typography variant="body2" color="info.main" sx={{ fontStyle: 'italic' }}>
                Note: Backend API must be implemented to show real driver performance data.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="600" mb={3}>
            Top {topPerformers.length} Performers (Last {periodFilter} Days)
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={topPerformers} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={100} />
              <RechartsTooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="earnings" fill="#2e7d32" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Wallet sx={{ mr: 2, color: '#007bff' }} />
            <Typography variant="h4" fontWeight="600">
              Driver Earnings Management
            </Typography>
          </Box>
        </Box>

        {/* Alert */}
        {alert.show && (
          <Alert 
            severity={alert.type} 
            sx={{ mb: 3 }}
            onClose={() => setAlert({ ...alert, show: false })}
          >
            {alert.message}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Overview" />
            <Tab label="Driver Details" />
            <Tab label="Top Performers" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {tabValue === 0 && (
          <Box>
            {renderSummaryCards()}
            {renderDriversTable()}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            {renderDriverDetails()}
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            {renderTopPerformers()}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DriverEarningsPanel;
